import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, Search, UserRoundCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useDebounce } from '@/hooks/useDebounce';
import { getUsers, updateOrganisationProfile } from '@/features/users/api';
import type { User } from '@/features/users/types';

interface AssignTechnicianDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string;
  accountName: string;
  organisationId: string;
}

function userId(user: User): string {
  return user.id || user.pg_id || '';
}

function userName(user: User): string {
  return [user.firstname, user.lastname].filter(Boolean).join(' ') || user.email;
}

function isAssigned(user: User, organisationId: string): boolean {
  return (user.organisations || []).some((profile) => {
    const organisation = profile.organisation;
    return (typeof organisation === 'string' ? organisation : organisation?._id) === organisationId;
  });
}

export function AssignTechnicianDialog({
  open,
  onOpenChange,
  accountId,
  accountName,
  organisationId,
}: AssignTechnicianDialogProps) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const debouncedSearch = useDebounce(search.trim(), 300);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setSelectedUserId('');
    }
  }, [open]);

  const techniciansQuery = useQuery({
    queryKey: ['assignable-technicians', debouncedSearch],
    queryFn: () => getUsers({
      search: debouncedSearch || undefined,
      enabled: 'true',
      employment_type: 'employee,contractor',
      sortKey: 'firstname',
      sortDirection: 1,
      pageNumber: 0,
      pageLength: 50,
    }),
    enabled: open && Boolean(organisationId),
    staleTime: 30_000,
  });

  const technicians = useMemo(
    () => techniciansQuery.data?.users || [],
    [techniciansQuery.data?.users],
  );
  const selectedTechnician = useMemo(
    () => technicians.find((user) => userId(user) === selectedUserId),
    [selectedUserId, technicians],
  );

  const assignMutation = useMutation({
    mutationFn: async (technician: User) => {
      const pgId = userId(technician);
      if (!pgId) throw new Error('This employee is missing a PostgreSQL user ID');

      await updateOrganisationProfile(pgId, organisationId, {
        admin: 'standard user',
        usergroups: [],
        sensorgroups: [],
        tags: [],
        description: 'Field technician sensor access',
      });
      return technician;
    },
    onSuccess: async (technician) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['account-users', accountId] }),
        queryClient.invalidateQueries({ queryKey: ['users'] }),
        queryClient.invalidateQueries({ queryKey: ['assignable-technicians'] }),
      ]);
      toast.success(`${userName(technician)} assigned to ${accountName}`);
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error('Could not assign technician', {
        description: error instanceof Error ? error.message : 'Unexpected error',
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col overflow-hidden p-0">
        <DialogHeader className="border-b px-6 pb-5 pt-6">
          <DialogTitle className="flex items-center gap-2">
            <UserRoundCheck className="h-5 w-5 text-atek-blue" />
            Assign technician
          </DialogTitle>
          <DialogDescription>
            Give an existing ATEK employee access to every sensor in {accountName}.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-hidden px-6 py-5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <Input
              aria-label="Search technicians"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name or email"
              className="h-12 pl-10 pr-10 text-base"
              autoComplete="off"
            />
            {search && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Clear technician search"
                className="absolute right-1 top-1 h-10 w-10"
                onClick={() => setSearch('')}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            Enabled employees and contractors are shown. Access takes effect as soon as you assign them.
          </p>

          <div className="max-h-[46vh] min-h-48 overflow-y-auto rounded-xl border" role="listbox" aria-label="Technicians">
            {techniciansQuery.isLoading && (
              <div className="space-y-2 p-3">
                {Array.from({ length: 4 }, (_, index) => (
                  <Skeleton key={index} className="h-16 w-full rounded-lg" />
                ))}
              </div>
            )}

            {techniciansQuery.isError && (
              <div className="flex min-h-48 flex-col items-center justify-center gap-3 p-6 text-center">
                <p className="text-sm text-destructive">Could not load employees.</p>
                <Button type="button" variant="outline" onClick={() => techniciansQuery.refetch()}>
                  Try again
                </Button>
              </div>
            )}

            {!techniciansQuery.isLoading && !techniciansQuery.isError && technicians.length === 0 && (
              <div className="flex min-h-48 items-center justify-center p-6 text-center text-sm text-muted-foreground">
                No matching enabled employee or contractor.
              </div>
            )}

            {!techniciansQuery.isLoading && !techniciansQuery.isError && technicians.map((technician) => {
              const pgId = userId(technician);
              const alreadyAssigned = isAssigned(technician, organisationId);
              const selected = pgId !== '' && pgId === selectedUserId;

              return (
                <button
                  key={pgId || technician._id || technician.email}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  disabled={alreadyAssigned || !pgId || assignMutation.isPending}
                  onClick={() => setSelectedUserId(pgId)}
                  className="flex min-h-16 w-full items-center gap-3 border-b px-4 py-3 text-left last:border-b-0 hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-atek-blue disabled:cursor-not-allowed disabled:bg-muted/30"
                >
                  <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 ${selected ? 'border-atek-blue bg-atek-blue text-white' : 'border-slate-300 bg-white'}`}>
                    {selected && <Check className="h-4 w-4" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-foreground">{userName(technician)}</span>
                    <span className="block truncate text-sm text-muted-foreground">
                      {technician.email}
                      {technician.position ? ` · ${technician.position}` : ''}
                    </span>
                  </span>
                  {alreadyAssigned ? (
                    <Badge variant="secondary">Already assigned</Badge>
                  ) : technician.employment_type ? (
                    <Badge variant="outline" className="capitalize">{technician.employment_type}</Badge>
                  ) : null}
                </button>
              );
            })}
          </div>

          {(techniciansQuery.data?.total || 0) > technicians.length && (
            <p className="text-xs text-muted-foreground">
              Showing the first {technicians.length} matches. Refine the search to find someone else.
            </p>
          )}
        </div>

        <DialogFooter className="border-t bg-muted/20 px-6 py-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={assignMutation.isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => selectedTechnician && assignMutation.mutate(selectedTechnician)}
            disabled={!selectedTechnician || assignMutation.isPending}
            className="min-w-40"
          >
            {assignMutation.isPending ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Assigning...</>
            ) : (
              'Assign to account'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
